import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, CheckCircle2, XCircle, ArrowLeft, LogOut, Loader2, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { ASSETS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ bookings: [], payments: [] });
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/me/bookings`);
        setData({ bookings: res.data.bookings || [], payments: res.data.payments || [] });
      } catch (e) {
        /* handled by empty state */
      }
      setBusy(false);
    })();
  }, [loading, user, navigate]);

  if (loading || busy) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#D5B876]" />
      </div>
    );
  }

  if (!user) return null;

  const initials = (user.name || user.email || "?").trim()[0].toUpperCase();

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0B302F]">
      <header className="border-b border-[#EAE4DA] bg-[#FDFBF7]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-home-link">
            <img
              src={ASSETS.logo}
              alt="Bloom & Heal"
              className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D5B876]/40"
            />
            <div className="font-serif text-xl text-[#0B302F]">Bloom &amp; Heal</div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#0B302F]/70 hover:text-[#0B302F]"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Home
            </Link>
            <Button
              onClick={logout}
              data-testid="dashboard-logout"
              variant="outline"
              className="rounded-full border-[#0B302F] text-[#0B302F] hover:bg-[#F9EAE8] bg-transparent text-xs tracking-[0.18em] uppercase h-9 px-4"
            >
              <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-5 mb-10">
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="h-16 w-16 rounded-full object-cover ring-1 ring-[#D5B876]/40"
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-[#0B302F] text-[#FDFBF7] font-serif text-2xl flex items-center justify-center">
              {initials}
            </div>
          )}
          <div>
            <div className="eyebrow text-[#2A5C5A]">Welcome back</div>
            <div className="font-serif text-3xl sm:text-4xl text-[#0B302F]" data-testid="dashboard-user-name">
              {user.name || user.email}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Bookings */}
          <div className="rounded-[1.5rem] border border-[#EAE4DA] bg-white p-6 sm:p-8">
            <div className="flex items-center justify-between mb-5">
              <div className="eyebrow text-[#2A5C5A]">Your sessions</div>
              <Link
                to="/#top"
                className="text-xs tracking-[0.18em] uppercase text-[#2A5C5A] hover:text-[#0B302F]"
              >
                Book new
              </Link>
            </div>
            {data.bookings.length === 0 ? (
              <EmptyState icon={Calendar} label="No sessions yet" />
            ) : (
              <ul className="space-y-3" data-testid="dashboard-bookings-list">
                {data.bookings.map((b) => (
                  <li
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className="flex items-start justify-between gap-3 p-4 rounded-2xl border border-[#EAE4DA] bg-[#FDFBF7]"
                  >
                    <div>
                      <div className="font-serif text-lg text-[#0B302F] leading-tight flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-[#2A5C5A]" strokeWidth={1.5} />
                        {b.preferred_date} <span className="text-[#0B302F]/50">•</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-4 w-4 text-[#2A5C5A]" strokeWidth={1.5} />
                          {b.preferred_time}
                        </span>
                      </div>
                      <div className="mt-1 text-xs tracking-wide text-[#0B302F]/60 uppercase">
                        {b.session_mode || "online"} • booked as {b.name}
                      </div>
                      {b.message ? (
                        <p className="mt-2 text-sm text-[#0B302F]/70 leading-relaxed">{b.message}</p>
                      ) : null}
                    </div>
                    <span className="text-[10px] text-[#2A5C5A] tracking-[0.18em] uppercase">
                      {new Date(b.created_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Payments */}
          <div className="rounded-[1.5rem] border border-[#EAE4DA] bg-white p-6 sm:p-8">
            <div className="eyebrow text-[#2A5C5A] mb-5">Your remedy payments</div>
            {data.payments.length === 0 ? (
              <EmptyState icon={IndianRupee} label="No payments yet" />
            ) : (
              <ul className="space-y-3" data-testid="dashboard-payments-list">
                {data.payments.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-start justify-between gap-3 p-4 rounded-2xl border border-[#EAE4DA] bg-[#FDFBF7]"
                  >
                    <div>
                      <div className="font-serif text-lg text-[#0B302F] flex items-center gap-2">
                        <IndianRupee className="h-4 w-4 text-[#2A5C5A]" strokeWidth={1.5} />
                        {p.amount_inr} {p.currency}
                      </div>
                      <div className="mt-1 text-xs tracking-wide uppercase text-[#0B302F]/60">
                        {p.status === "paid" ? (
                          <span className="inline-flex items-center gap-1 text-[#2A5C5A]">
                            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.6} /> Paid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#D5B876]">
                            <XCircle className="h-3.5 w-3.5" strokeWidth={1.6} /> {p.status}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] text-[#2A5C5A] tracking-[0.18em] uppercase">
                      {new Date(p.created_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ icon: Icon, label }) {
  return (
    <div className="text-center py-10">
      <Icon className="h-6 w-6 text-[#D5B876] mx-auto" strokeWidth={1.5} />
      <div className="mt-3 text-sm text-[#0B302F]/60">{label}</div>
    </div>
  );
}
